import React, { useMemo, useState } from 'react';
import { MagnifyingGlassIcon, UserCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { matchesSearch } from '../../utils/search';
import type { PlayerAccountOption } from '../../types/player';

interface AccountPickerProps {
  id?: string;
  /** A választható fiókok (pl. a szülőfiókok). */
  options: PlayerAccountOption[];
  /** A kiválasztott fiók azonosítója ('' = nincs hozzárendelve). */
  value: string;
  onChange: (value: string) => void;
  /** Ezek nem választhatók (pl. az első szülő a második szülőnél). */
  excludeIds?: string[];
  placeholder?: string;
}

/** Ennyi találat jelenik meg; többnél a keresés szűkítését kérjük. */
const MAX_RESULTS = 20;

/**
 * Fiókválasztó nagy létszámra: a több száz elemes legördülő helyett névre vagy
 * emailre keres, a kiválasztott fiók pedig felül látszik, egy gombbal levehető.
 */
const AccountPicker: React.FC<AccountPickerProps> = ({
  id,
  options,
  value,
  onChange,
  excludeIds = [],
  placeholder = 'Keress névre vagy email címre...',
}) => {
  const [search, setSearch] = useState('');
  const selected = options.find((o) => o.id.toString() === value);

  const matches = useMemo(() => {
    if (!search.trim()) return [];
    return options
      .filter((o) => !excludeIds.includes(o.id.toString()) && matchesSearch(search, o.name, o.email))
      .sort((a, b) => a.name.localeCompare(b.name, 'hu'));
  }, [options, search, excludeIds]);

  if (value) {
    return (
      <div className="flex items-center gap-2 rounded-md border border-gray-300 bg-gray-50 px-3 py-2">
        <UserCircleIcon className="h-5 w-5 shrink-0 text-gray-400" />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-medium text-gray-900">
            {selected?.name ?? 'Hozzárendelt fiók'}
            {selected && !selected.isActive && <span className="ml-1 text-xs font-normal text-gray-500">(inaktív)</span>}
          </span>
          {selected?.email && <span className="block truncate text-xs text-gray-500">{selected.email}</span>}
        </span>
        <button
          type="button"
          onClick={() => onChange('')}
          aria-label="Hozzárendelés megszüntetése"
          title="Hozzárendelés megszüntetése"
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-gray-500 hover:bg-gray-200 hover:text-gray-700"
        >
          <XMarkIcon className="h-4 w-4" />
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-1">
      <div className="relative">
        <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
        <input
          id={id}
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={placeholder}
          autoComplete="off"
          className="block w-full rounded-md border border-gray-300 py-2 pl-9 pr-3 text-base focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 sm:text-sm"
        />
      </div>

      {search.trim() ? (
        <div className="max-h-56 overflow-y-auto rounded-md border border-gray-200">
          {matches.length === 0 ? (
            <p className="px-3 py-2 text-sm text-gray-500">Nincs ilyen fiók.</p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {matches.slice(0, MAX_RESULTS).map((o) => (
                <li key={o.id}>
                  <button
                    type="button"
                    onClick={() => {
                      onChange(o.id.toString());
                      setSearch('');
                    }}
                    className="block w-full px-3 py-2 text-left text-sm hover:bg-gray-50"
                  >
                    <span className="font-medium text-gray-900">{o.name}</span>
                    {!o.isActive && <span className="ml-1 text-xs text-gray-500">(inaktív)</span>}
                    <span className="block text-xs text-gray-500">{o.email}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          {matches.length > MAX_RESULTS && (
            <p className="border-t border-gray-100 px-3 py-2 text-xs text-gray-500">
              További {matches.length - MAX_RESULTS} találat - pontosítsd a keresést.
            </p>
          )}
        </div>
      ) : (
        <p className="text-xs text-gray-500">Nincs hozzárendelve. Gépeld be a nevet vagy az email címet.</p>
      )}
    </div>
  );
};

export default AccountPicker;
